import { useState, useEffect, useContext } from "react";
import HeaderDashboard from "../../Components/HeaderDashboard";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../Routes/AuthProvider"
import { GetBusTransactionDetails } from "../../Services/BusAPI";
import BusEmailTicket from './BusEmailTicket';
import SmsBusTicket from './SmsBusTicket';
import { TicketDownload } from "../../Services/BusAPI";
import { GetTravelDocuments } from "../../Services/API";
import TravelDocument from './TravelDocument';

const TransactionsDetails = () => {
  const { name, userName, roles, corporateId } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  function getParamIgnoreCase(key) {
    for (const [k, v] of queryParams.entries()) {
      if (k.toLowerCase() === key.toLowerCase()) {
        return v;
      }
    }
    return null;
  }
  const referenceNo = getParamIgnoreCase("ReferenceNo");
  const key = getParamIgnoreCase("key");
  const clientCode = getParamIgnoreCase("clientCode");

  const [bustransactionDetails, setBusTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");


  //  Debug logs
  useEffect(() => {
    //console.log("URL:", location.search);
    //console.log("ReferenceNo:", referenceNo);
    //console.log("ClientCode:", clientCode);
    //console.log("Key:", key);

    if (referenceNo && clientCode && key) {
      fetchBusTransactionDetails();
    } else {
      console.warn("Missing required params in URL");
    }
  }, [referenceNo, clientCode, key]);

  //  API Call
  const fetchBusTransactionDetails = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        ClientCode: clientCode,
        Key: key,
        referenceNo: referenceNo,
      };

      //console.log("FINAL API BODY:", params);
      const response = await GetBusTransactionDetails(params);
      //console.log("FULL RESPONSE OBJECT:", JSON.stringify(response, null, 2));

      //console.log("API Response:", response);

      setBusTransactionDetails(response);

    } catch (err) {
      console.error("API ERROR:", err);
      setError("Failed to load Transaction details.");
    } finally {
      setLoading(false);
    }
  };

  //  Safe Data
  const booking = bustransactionDetails?.booking;
  const bookingPoints = bustransactionDetails?.bookingPoints;
  const passengers = bustransactionDetails?.passengers || [];
  const payments = bustransactionDetails?.payments[0] || [];
  const fares = bustransactionDetails?.fares[0] || [];


  const parseCustomDate = (dateTime) => {
    if (!dateTime) return null;

    // Fix format: "Apr 29 2026  1:52AM" → "Apr 29 2026 1:52 AM"
    const cleaned = dateTime
      .replace(/\s+/g, " ")         // remove extra spaces
      .replace(/(AM|PM)$/i, " $1"); // add space before AM/PM

    const date = new Date(cleaned);
    return isNaN(date) ? null : date;
  };

  const formatTime = (dateTime) => {
    const date = parseCustomDate(dateTime);
    if (!date) return "-";

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateTime) => {
    const date = parseCustomDate(dateTime);
    if (!date) return "-";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  const handlePrintTicket = () => {
    const url = `/reservations/bus/BusPrintTicket?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`;
    const printWindow = window.open(url, "_blank");

    if (printWindow) {
      printWindow.focus();
    }
  };

  const handleInvoiceTicket = () => {
    const url = `/reservations/bus/PrintInvoice?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`;
    const printWindow = window.open(url, "_blank");

    if (printWindow) {
      printWindow.focus();
    }
  };
  const fare = fares?.[0] || {};
  ////console.log("FARES:", fares);

  const totalBaseFare =
    (fares?.baseFare || 0) + (fares.gst || 0) + (fares?.companyMarkUp || 0);

  const policyString = booking?.cancellationPolicy || "";

  const policies = policyString
    .split(";")
    .filter(Boolean)
    .map((item) => {
      const parts = item.split(":");

      const from = parts[0]?.trim();   // e.g. "48hours"
      const to = parts[1]?.trim();     // e.g. "72hours of bus start time"
      const charge = parts[2]?.trim(); // e.g. "30"

      let label = "";

      if (from === "Above") {
        label = `Within ${to}`;
      } else if (from === "0hours") {
        label = `Above 0 hours`;
      } else {
        label = `Between ${from} to ${to}`;
      }

      return {
        label,
        charge,
      };
    });

  const handleTicketdownload = async () => {
    try {
      setLoading(true);
      const reqParms = {
        referenceNo: bustransactionDetails?.referenceNo,
        //trackId: flightDetails?.airBookingResponse[0]?.flightDetails[0]?.trackID,
        type: "Ticket"
      };

      const response = await TicketDownload(reqParms);

      if (response && response.data) {
        const blob = new Blob([response.data], {
          type: "application/pdf",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "ETicket.pdf";

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match?.[1]) fileName = match[1];
        }

        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert("Failed to download ticket.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error downloading ticket.");
    } finally {
      setLoading(false);
    }
  };

  const [documents, setDocuments] = useState([]);
  const [showDocuments, setShowDocuments] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const handleGetDocuments = async () => {
    try {
      setLoadingDocuments(true);

      const request = {
        ReferenceNo: booking?.referenceNo,
        ProductType: "Bus",
      };

      const result = await GetTravelDocuments(request);
      //console.log("GetTravelDocuments", result);
      setDocuments(result || []);
      setShowDocuments(true);
    } catch (error) {
      console.error(error);
      setDocuments([]);
      setShowDocuments(true);
    } finally {
      setLoadingDocuments(false);
    }
  };

  return (
    <>
      {loading && (
        <p class="text-center load"><span class="loader"></span></p>
      )}

      <HeaderDashboard></HeaderDashboard>
      <div className="toolbar py-2" id="kt_toolbar">
        <div id="kt_toolbar_container" className="container-fluid d-flex align-items-center">
          <div className="flex-grow-1 flex-shrink-0 me-5">
            <div data-kt-swapper="true" data-kt-swapper-mode="prepend" data-kt-swapper-parent="{default: '#kt_content_container', 'lg': '#kt_toolbar_container'}" className="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
              <h1 className="d-flex align-items-center text-gray-900 fw-bold my-1 fs-3">Reservations</h1>
              <span className="h-20px border-gray-200 border-start mx-3"></span>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1">
                <li class="breadcrumb-item text-muted">Bus</li>
                <li class="breadcrumb-item">
                  <span class="bullet bg-gray-200 w-5px h-2px"></span>
                </li>
                <li class="breadcrumb-item text-gray-900">Bus Transaction Details</li>
              </ul>
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap">
            <div class="flex-shrink-0 me-2 d-none-mob">
              <ul class="nav" role="tablist">
                <li class="nav-item" role="presentation">
                  <a href="#" onClick={(e) => { e.preventDefault(); window.history.back(); }} >
                    <img src="/assets/images/back.png" width={30} />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <section className="wraper">
        <div className="box_shadow mt-14">
          {booking?.isVipUser === true && (
            <div class="alert alert-success" role="alert">
              <strong>VIP Traveller</strong>
            </div>
          )}
          {/* Booking Header */}
          <div className="row mamage_depo">
            <div className="col-lg-6">
              <h6 className="ruler">Bus Booking Details</h6>
            </div>
            {booking?.bookingStatus === "Confirmed" && (
              <div class="col-lg-6 text-right">
                <ul className='option_s'>

                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.open(
                          `/reservations/bus/BusPrintTicket?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`,
                          "_blank"
                        );
                      }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                      title="Print Ticket"
                    >
                      <img src="/assets/images/printer.png" alt="Print Ticket" />
                    </button>
                  </li>

                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.open(
                          `/reservations/bus/PrintInvoice?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`,
                          "_blank"
                        );
                      }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                      title="Invoice"
                    >
                      <img src="/assets/images/Invoice.png" alt="Invoice" />
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={handleTicketdownload}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                      title="Download Ticket"
                    >
                      <img
                        src="/assets/images/download.png"
                        alt="Download Ticket"
                      />
                    </button>
                  </li>
                  <li>
                    <a href={`/reservations/bus/CancellationBooking?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`}>
                      <h5><img title="Cancel Booking" alt="Cancel Booking" src="/assets/images/ticket.png" /> </h5>
                    </a>
                  </li>


                </ul>

              </div>
            )}
          </div>

          {/* Booking Details */}
          <div className="col-md-12">
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>FROM    -   TO</th>
                    <th>Reference No</th>
                    <th>Booking Status</th>
                    <th>PNR</th>
                    <th>Booking Date</th>
                    <th>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>{booking?.sourceName} - {booking?.destinationName}</th>
                    <td>{booking?.referenceNo}</td>
                    {booking?.bookingStatus === "Confirmed" ? (
                      <td style={{ color: "green" }}>
                        <b>{booking?.bookingStatus}</b>
                      </td>
                    ) : (
                      <td style={{ color: "red" }}>
                        <b>{booking?.bookingStatus}</b>
                      </td>
                    )}
                    <td>{booking?.pnr}</td>
                    <th> {formatDate(booking?.createdOn)} <span className="color"> {formatTime(booking?.createdOn)}</span></th>
                    {payments?.paymentStatus === "Success" ? (
                      <td style={{ color: "green" }}>
                        <b>{payments?.paymentStatus}</b>
                      </td>
                    ) : (
                      <td>
                        <b>{payments?.paymentStatus}</b>
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Travel Info */}
          <div className="col-md-12">
            <h6 className="ruler">Bus Details</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Operator</th>
                    <th>Bus Type</th>
                    <th>Departure Time</th>
                    <th>Arrival Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td> {booking?.operatorName}</td>
                    <td> {booking?.busType}</td>
                    <th>{formatDate(booking?.departureTime)}
                      <span className="color">{formatTime(booking?.departureTime)}</span>
                    </th>
                    <th>
                      {formatDate(booking?.arrivalTime)}
                      <span className="color">{formatTime(booking?.arrivalTime)}</span>
                    </th>


                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-md-12">
            <h6 className="ruler">Boarding Point</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>LandMark</th>
                    <th>Time</th>
                    <th>Phone No</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{bookingPoints?.boardingPointAddress || "-"}</td>
                    <td>{bookingPoints?.boardingPointLandMark || "-"}</td>
                    <th>{formatDate(bookingPoints?.boardingPointTime)}
                      <span className="color">{formatTime(bookingPoints?.boardingPointTime)}</span>
                    </th>
                    <td>{bookingPoints?.boardingPointPhone || "-"}</td>
                    <td>{bookingPoints?.boardingPointLocation || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-md-12">
            <h6 className="ruler">Dropping Point</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>LandMark</th>
                    <th>Time</th>
                    <th>Phone No</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{bookingPoints?.droppingPointAddress || "-"}</td>
                    <td>{bookingPoints?.droppingPointLandMark || "-"}</td>
                    <th>{formatDate(bookingPoints?.droppingPointTime)}
                      <span className="color">{formatTime(bookingPoints?.droppingPointTime)}</span>
                    </th>
                    <td>{bookingPoints?.droppingPointPhone || "-"}</td>
                    <td>{bookingPoints?.droppingPointLocation || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-md-12">
            <h6 className="ruler">Policy Details</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Policy Status</th>
                    <th>Policy Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{booking?.policyStatus || "-"}</td>
                    {/* <td>{booking?.policyReason || "-"}</td> */}
                    <td>{booking?.policyReason && booking.policyReason !== "null" ? booking.policyReason : "NA"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Cart Amendments */}
          {booking?.bookingStatus === "Confirmed" && (
            <div class="col-md-12 col-lg-12 mb-12">
              <h6 class="ruler">Cart Amendments</h6>
              <div class="row">

                <div className="col-md-3">
                  <div className='box_shadows text-left'>

                    <a
                      type="button"
                      onClick={handlePrintTicket}
                      style={{ background: "none", border: "none", padding: 0 }}
                    >
                      <h5><img src="/assets/images/printer.png" title='Print Ticket' alt="Print Ticket" /> Print Ticket</h5>
                      <p>Print your ticket for travel, Get a printable copy of your ticket</p>
                      <button className='btn-raise'>Print Ticket</button>
                    </a>
                  </div>
                </div>


                <div className="col-md-3">
                  <div className="box_shadows">
                    <div
                      onClick={handleInvoiceTicket}

                      style={{ cursor: "pointer" }}
                    >
                      <h5>
                        <img src="/assets/images/Invoice.png" alt="Invoice" /> Invoice
                      </h5>
                      <p>View & Manage the Invoice for Bus booking ticket.</p>
                      <button className="btn-raise" type="button">
                        View Invoice
                      </button>
                    </div>
                  </div>

                </div>


                <div className="col-md-3">
                  <div className="box_shadows">
                    <div className="w-100 d-block">
                      <h5>
                        <img src="/assets/images/Download.png" alt="Download Ticket" /> Download Ticket
                      </h5>
                      <p>Download the copy of your booked Bus ticket.</p>
                      <button
                        className="btn-raise"
                        type="button"
                        onClick={handleTicketdownload}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                </div>

                <div className='col-md-3'>
                  <div className='box_shadows'>
                    <a href='' data-bs-toggle="modal" data-bs-target="#EmailBusTicket" className=''>
                      <h5><img src="/assets/images/email.png" title='Email Ticket' alt="Email Ticket" /> Email Ticket</h5>
                      <p>Email the copy of ticket on my Email Address.</p>
                      <button className='btn-raise'>Email Ticket</button>
                    </a>
                  </div>
                </div>

                {/* <div className='col-md-3'>
                <div className='box_shadows'>
                  <a href='' data-bs-toggle="modal" data-bs-target="#SMSBusTicket" className=''>
                    <h5><img src="/assets/images/sms.png" title='SMS Ticket' alt="SMS Ticket" /> SMS Ticket</h5>
                    <p>Send the Flight ticket on my contact number by SMS.</p>
                    <button className='btn-raise'>SMS Ticket</button>
                  </a>
                </div>
              </div> */}
                <div class="col-md-3"><div class="box_shadows">
                  <a href={`/reservations/bus/CancellationBooking?ReferenceNo=${booking?.referenceNo}&clientCode=${clientCode}&key=${key}`}>
                    <h5><img title="Cancel Booking" alt="Cancel Booking" src="/assets/images/ticket.png" /> Cancel Booking</h5><p>Check cancellation charges and refund eligibility before request</p><button class="btn-raise">Raise Request</button></a>
                </div>
                </div>
              </div>
            </div>

          )}
          {/* Contact Info */}
          <div className="col-md-12">
            <h6 className="ruler">Contact Details</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Mobile Number</th>
                    <th>Email</th>
                    <th>Booking Created By</th>
                  </tr>
                  <tr>
                    <td>{booking?.mobile}</td>
                    <td>{booking?.emailId}</td>
                    <td>{booking?.createdBy}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Passenger Info */}
          <div className="col-md-12">
            <h6 className="ruler">Passenger Information</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Seat no</th>
                  </tr>
                </thead>
                <tbody>

                  {passengers.map((p, index) => (
                    <tr key={p.paxIndex}>
                      <td>{index + 1}</td>
                      <td>{`${p.title} ${p.firstName} ${p.lastName}`}</td>
                      <td>{p.gender}</td>
                      <td>{p.age}</td>
                      <td>{p.seatNo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>



          {/* Fare */}
          <div className="col-md-12">
            <h6 className="ruler">fare Detail</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Bus Fare (Incl. Taxes)</th>
                    <th>Management Fees</th>
                    <th>Management Fees GST</th>
                    <th>GRAND TOTAL</th>

                  </tr>
                  <tr>
                    <td>₹ {totalBaseFare}</td>
                    <td>₹ {fares.managementFees}</td>
                    <td>₹ {fares.managementFeesGST}</td>
                    <th>₹ {fares.totalFare}</th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment */}
          <div className="col-md-12">
            <h6 className="ruler">Payment</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Status</th>
                    <th>Mode</th>
                    <th>Amount Paid</th>

                  </tr>
                  <tr>
                    <th> <span className="color"> {payments?.paymentStatus}</span></th>
                    <td>{payments?.paymentMode || "-"}</td>
                    <th>₹ {payments.amountPaid}</th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Important Info */}
          <div className="col-lg-12 important_info">
            <h4>Cancellation Policy</h4>
            <p className="partialCancelation">
              Partial Cancellation is :{" "}
              <span
                style={{
                  color: booking?.partialCancellationAllowed ? "green" : "red",
                }}
              >
                {booking?.partialCancellationAllowed ? "Allowed" : "Not Allowed"}
              </span>
            </p>


            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Cancellation time</th>
                    <th>Cancellation charges</th>
                  </tr>
                </thead>

                <tbody>
                  {policies.length > 0 ? (
                    policies.map((p, index) => (
                      <tr key={index}>
                        <td>{p.label} Hrs</td>
                        <td>{p.charge}%</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2">No Cancellation Policy Available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {["TMC Manager", "TMC Operations", "Corp Travel Manager"].some(role => roles?.includes(role)) && (
            <div className="col-md-12 col-lg-12">
              <div className="row align-items-center mb-3">
                <div className="col-md-6">
                  <h6 className="ruler mb-0">Travel Documents</h6>
                </div>
                <div className="col-md-6 text-md-end text-start mt-2 mt-md-0">
                  <div className="d-inline-block">
                    <a
                      href="#"
                      className="btn btn-primary"
                      data-bs-toggle="modal"
                      data-bs-target="#TravelDocuments"
                      onClick={(e) => e.preventDefault()}
                    >
                      + Upload Document
                    </a>
                  </div>
                </div>
              </div>

              {!showDocuments && (
                <div className="row mb-3">
                  <div className="col-12 d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleGetDocuments}
                      disabled={loadingDocuments}
                    >
                      {loadingDocuments ? "Loading..." : "Get Documents"}
                    </button>
                  </div>
                </div>
              )}

              {showDocuments && documents.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Document Type</th>
                        <th>Document</th>
                        <th>Created By</th>
                        <th>Created On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.id}>
                          <td>{doc.productType}</td>
                          <td>{doc.documentType}</td>
                          <td>
                            <a
                              href={`${process.env.REACT_APP_API_BASE_URL}/uploads/TravelDocuments/${doc.documentPath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </a>
                          </td>
                          <td>{doc.createdBy}</td>
                          <td>{new Date(doc.createdOn).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                showDocuments && (
                  <div className="alert alert-info text-center">
                    No documents found.
                  </div>
                )
              )}
            </div>
          )}

        </div>
      </section>




      <div className="modal fade" id="EmailBusTicket" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">

            <div className="modal-header">
              <h4 className="modal-title">Send Ticket to Email Address</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">

              <BusEmailTicket
                referenceNo={booking?.referenceNo}
                trackId={booking?.referenceNo}
              />

            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="SMSBusTicket" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">

            <div className="modal-header">
              <h4 className="modal-title">Send Ticket by SMS</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <SmsBusTicket></SmsBusTicket>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="TravelDocuments" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">

            <div className="modal-header">
              <h4 className="modal-title">Upload Travel Document</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <TravelDocument
                referenceNo={booking?.referenceNo}
                trackId={booking?.referenceNo}
                userName={userName}
              />

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionsDetails;